import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { createSessionsFromTeacherSelection } from '../../services/sessionScheduler';
import {
  getStudentScopedStorageItem,
  PENDING_TEACHER_SELECTION_STORAGE_KEY,
  removeStudentScopedStorageItem,
  SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
  SELECTED_TEACHERS_STORAGE_KEY,
  setStudentScopedStorageItem,
} from '../../utils/studentSelectionStorage';

interface PendingSubscriptionPurchase {
  planId: string;
  planName: string;
  amount: number;
  currency?: string;
  billingCycle?: 'monthly' | 'annual';
  mode?: 'new' | 'upgrade';
  role?: 'TEACHER' | 'STUDENT' | 'RIDES';
}

interface PendingRidesDriverPlanPurchase {
  planId: string;
  planName: string;
  amount: number;
  currency?: string;
}

interface LocalTeacherSelection {
  teacher: {
    id: string;
    userId?: string;
    name: string;
    subject: string;
    location: string;
    rating: number;
    rate: number;
    image: string;
    about: string;
    experience: string;
    availabilitySlots?: string[];
  };
  preference: {
    subjectId?: string;
    subjectName: string;
    classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
    offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
    frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
    preferredTimeSlot?: string;
    startDate: string;
    endDate: string;
    notes: string;
  };
  selectedAt: string;
}

interface PendingTeacherSelectionPurchase {
  amount: number;
  selectionRequest: {
    teacherId: string;
    teacherName: string;
    subject: string;
    subjectId?: string;
    classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
    offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
    frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
    preferredTimeSlot?: string;
    startDate: string;
    endDate: string;
    notes?: string;
    subscriptionPlanId: string;
  };
  localSelection: LocalTeacherSelection;
  nextSelections: LocalTeacherSelection[];
  successMessage: string;
  paymentTransactionId?: string;
}

const PENDING_SUBSCRIPTION_KEY = 'pendingSubscriptionPurchase';
const PENDING_RIDES_DRIVER_PLAN_KEY = 'pendingRidesDriverPlanPurchase';

const WalletTopupPayment: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialAmount = (location.state as any)?.amount || '';
  const sourceRole = (location.state as any)?.role as 'TEACHER' | 'STUDENT' | 'RIDES' | undefined;
  const currentStudentUserId = String(user?.id || '').trim();

  const [amount, setAmount] = useState<string>(initialAmount ? String(initialAmount) : '');
  const [transactionId, setTransactionId] = useState('');
  const [paymentLinkId, setPaymentLinkId] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const initiateTopup = useCallback(async (rawAmount: string, autoRedirect = false) => {
    const topupAmount = Number(rawAmount);
    if (!Number.isFinite(topupAmount) || topupAmount <= 0) {
      alert('Enter a valid amount.');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post<{
        transactionId: string;
        paymentLinkId: string;
        checkoutUrl: string;
      }>('/teachers/wallet/topup/initiate', { amount: topupAmount });
      setTransactionId(response.data.transactionId);
      setPaymentLinkId(response.data.paymentLinkId || '');
      setCheckoutUrl(response.data.checkoutUrl || '');
      if (autoRedirect && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to initiate top-up.';
      alert(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyTopup = useCallback(async (linkIdArg?: string) => {
    const linkId = (linkIdArg || paymentLinkId).trim();
    if (!linkId) return;
    try {
      setLoading(true);
      const response = await api.post<{ transactionId: string }>('/teachers/wallet/topup/verify', {
        paymentLinkId: linkId,
      });
      setTransactionId(response.data?.transactionId || transactionId);

      const pendingTeacherSelectionRaw = getStudentScopedStorageItem(
        PENDING_TEACHER_SELECTION_STORAGE_KEY,
        currentStudentUserId,
      );
      if (pendingTeacherSelectionRaw) {
        try {
          const pendingTeacherSelection = JSON.parse(
            pendingTeacherSelectionRaw,
          ) as PendingTeacherSelectionPurchase;
          const teacherId = String(pendingTeacherSelection?.selectionRequest?.teacherId || '').trim();
          const amount = Number(pendingTeacherSelection?.amount || 0);
          if (teacherId && Number.isFinite(amount) && amount > 0) {
            let paymentTransactionId = String(
              pendingTeacherSelection.paymentTransactionId || '',
            ).trim();

            if (!paymentTransactionId) {
              const teacherPaymentResponse = await api.post<{ transactionId: string }>(
                '/teachers/wallet/pay',
                {
                  teacherId,
                  amount,
                  note: `Teacher plan payment: ${pendingTeacherSelection.selectionRequest.subscriptionPlanId}`,
                },
              );
              paymentTransactionId = String(teacherPaymentResponse.data?.transactionId || '').trim();
              if (!paymentTransactionId) {
                throw new Error('Teacher payment transaction id was not returned');
              }
              setStudentScopedStorageItem(
                PENDING_TEACHER_SELECTION_STORAGE_KEY,
                currentStudentUserId,
                JSON.stringify({
                  ...pendingTeacherSelection,
                  paymentTransactionId,
                }),
              );
            }

            await api.post('/teachers/select', {
              ...pendingTeacherSelection.selectionRequest,
              subscriptionTransactionId: paymentTransactionId,
            });

            setStudentScopedStorageItem(
              SELECTED_TEACHERS_STORAGE_KEY,
              currentStudentUserId,
              JSON.stringify(pendingTeacherSelection.nextSelections),
            );
            setStudentScopedStorageItem(
              SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
              currentStudentUserId,
              JSON.stringify(pendingTeacherSelection.localSelection),
            );
            createSessionsFromTeacherSelection(pendingTeacherSelection.localSelection);
            removeStudentScopedStorageItem(
              PENDING_TEACHER_SELECTION_STORAGE_KEY,
              currentStudentUserId,
            );
            alert(pendingTeacherSelection.successMessage || 'Teacher selection completed successfully.');
            navigate('/student/dashboard');
            return;
          }
        } catch (teacherSelectionError: any) {
          const message =
            teacherSelectionError?.response?.data?.message ||
            teacherSelectionError?.message ||
            'Teacher selection activation failed after top-up.';
          alert(message);
          return;
        }
      }

      const pendingRaw = localStorage.getItem(PENDING_SUBSCRIPTION_KEY);
      const pendingRidesRaw = localStorage.getItem(PENDING_RIDES_DRIVER_PLAN_KEY);
      if (pendingRidesRaw) {
        try {
          const pendingRides = JSON.parse(pendingRidesRaw) as PendingRidesDriverPlanPurchase;
          if (pendingRides?.planId) {
            await api.post('/rides/drivers/me/subscribe', {
              planId: pendingRides.planId,
              paymentMethodId: 'WALLET',
            });
            localStorage.removeItem(PENDING_RIDES_DRIVER_PLAN_KEY);
            alert('Wallet top-up successful and rides driver plan activated.');
            navigate('/rides/dashboard');
            return;
          }
        } catch (ridesError: any) {
          const msg = ridesError?.response?.data?.message || 'Rides driver plan activation failed after top-up.';
          alert(msg);
        }
      }

      if (pendingRaw) {
        try {
          const pending = JSON.parse(pendingRaw) as PendingSubscriptionPurchase;
          if (pending?.planId) {
            if (pending.role === 'TEACHER') {
              await api.post(`/teachers/subscription/subscribe/${pending.planId}`, {
                paymentMethodId: 'WALLET',
              });
            } else if (pending.mode === 'upgrade') {
              await api.put(`/subscriptions/me/upgrade/${pending.planId}`);
            } else {
              await api.post('/subscriptions', {
                planId: pending.planId,
                billingCycle: pending.billingCycle || 'monthly',
              });
            }
            localStorage.removeItem(PENDING_SUBSCRIPTION_KEY);
            alert('Wallet top-up successful and subscription activated.');
            navigate(pending.role === 'TEACHER' ? '/teacher/subscription-plans' : '/subscriptions');
            return;
          }
        } catch (subscriptionError: any) {
          const msg = subscriptionError?.response?.data?.message || 'Subscription activation failed after top-up.';
          alert(msg);
        }
      }

      alert('Wallet top-up successful.');
      navigate(sourceRole === 'TEACHER' ? '/teacher/payments' : sourceRole === 'RIDES' ? '/rides/dashboard' : '/subscriptions');
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Payment is pending verification.';
      alert(message);
    } finally {
      setLoading(false);
    }
  }, [currentStudentUserId, navigate, paymentLinkId, sourceRole, transactionId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkId = params.get('razorpay_payment_link_id') || '';
    const linkStatus = params.get('razorpay_payment_link_status') || '';

    if (linkId && linkStatus.toLowerCase() === 'paid') {
      void verifyTopup(linkId);
      return;
    }

    if (initialAmount) {
      void initiateTopup(String(initialAmount), true);
    }
  }, [initialAmount, initiateTopup, location.search, verifyTopup]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900">Wallet Top-up Payment</h2>
        <p className="mt-1 text-sm text-gray-500">You will be redirected to Razorpay payment page</p>

        <div className="mt-4 flex gap-2">
          <input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-48 rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Amount"
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => initiateTopup(amount)}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Proceed to Pay
          </button>
        </div>
      </div>

      {checkoutUrl && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900">Payment Link Generated</h3>
          <p className="mt-2 text-sm text-gray-600 break-all">{checkoutUrl}</p>
          <p className="mt-3 text-xs text-gray-500">Transaction ID: {transactionId || '-'}</p>
          <p className="mt-1 text-xs text-gray-500">Payment Link ID: {paymentLinkId || '-'}</p>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => window.open(checkoutUrl, '_self')}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Open Payment Page
            </button>
            <button
              type="button"
              disabled={loading || !paymentLinkId}
              onClick={() => verifyTopup()}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              Verify Payment
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletTopupPayment;
